import { useCallback, useEffect, useRef } from "react";

export const LEAVE_GUARD_STATE_KEY = "cfLeaveGuard";

const isGuardEntry = (state) => Boolean(state?.[LEAVE_GUARD_STATE_KEY]);

const randomKey = () => Math.random().toString(36).slice(2, 10);

export const isOnLeaveGuardEntry = () => isGuardEntry(window.history.state);

export function useLeaveGuard({ when, onAttempt }) {
  const whenRef = useRef(when);
  const onAttemptRef = useRef(onAttempt);
  const leavingRef = useRef(false);
  const pendingDeltaRef = useRef(null);
  const guardRef = useRef(null);

  useEffect(() => {
    whenRef.current = when;
    onAttemptRef.current = onAttempt;
  });

  useEffect(() => {
    if (!when) return undefined;
    const warn = (event) => {
      if (leavingRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [when]);

  useEffect(() => {
    if (!when || isGuardEntry(window.history.state)) return;
    const original = window.history.state || {};
    const idx = typeof original.idx === "number" ? original.idx + 1 : undefined;
    const state = {
      ...original,
      key: randomKey(),
      idx,
      [LEAVE_GUARD_STATE_KEY]: true,
    };
    guardRef.current = {
      state,
      url: window.location.href,
      originalKey: original.key,
    };
    window.history.pushState(state, "", window.location.href);
  }, [when]);

  useEffect(() => {
    const handlePopState = (event) => {
      const guard = guardRef.current;
      if (leavingRef.current || !guard || isGuardEntry(event.state)) return;

      const landedOnOriginal =
        event.state?.key === guard.originalKey &&
        window.location.href === guard.url;

      if (!whenRef.current) {
        if (landedOnOriginal) {
          event.stopImmediatePropagation();
          window.history.back();
        }
        return;
      }

      event.stopImmediatePropagation();
      pendingDeltaRef.current = landedOnOriginal ? -2 : -1;
      const targetIdx = event.state?.idx;
      if (typeof targetIdx === "number") {
        guard.state = { ...guard.state, idx: targetIdx + 1 };
      }
      window.history.pushState(guard.state, "", guard.url);
      onAttemptRef.current?.();
    };

    window.addEventListener("popstate", handlePopState, true);
    return () => window.removeEventListener("popstate", handlePopState, true);
  }, []);

  const stay = useCallback(() => {
    pendingDeltaRef.current = null;
  }, []);

  const leave = useCallback((fallback) => {
    const delta = pendingDeltaRef.current;
    pendingDeltaRef.current = null;
    leavingRef.current = true;
    if (delta) {
      window.history.go(delta);
      return;
    }
    fallback?.();
  }, []);

  return { stay, leave };
}

export default useLeaveGuard;
